import type { StudyStatus } from "@repo/db/constants";

/**
 * Whether the opportunities list offers a status as a filter. Key order is the
 * order the filter chips render in, and the list page's `?status=` param accepts
 * exactly the true keys. Anything else is dropped, falling back to the saved
 * cookie and then to the default set. Lives here rather than in `apps/web` so
 * the `webPaths.orgOpportunities` builder and the page that parses the param
 * read the same set. Adding a StudyStatus without classifying it fails the
 * `satisfies` check.
 */
const STUDY_STATUS_FILTERABILITY = {
  DRAFT: true,
  PRE_RECRUITING: true,
  ACTIVE: true,
  PAUSED: true,
  FULFILLED: true,
  COMPLETED: true,
  STOPPED: true,
  // Transient state owned by the quote-launch path; a researcher never filters on it.
  CREATING: false,
  EXPIRED: false,
  CANCELLED: false,
  FAILED: false,
} as const satisfies Record<StudyStatus, boolean>;

type Filterability = typeof STUDY_STATUS_FILTERABILITY;

export type FilterableStudyStatus = {
  [K in keyof Filterability]: Filterability[K] extends true ? K : never;
}[keyof Filterability];

export const FILTERABLE_STUDY_STATUSES: readonly FilterableStudyStatus[] = (
  Object.keys(STUDY_STATUS_FILTERABILITY) as (keyof Filterability)[]
).filter(
  (status): status is FilterableStudyStatus =>
    STUDY_STATUS_FILTERABILITY[status],
);
